import { Box, IconButton, Tooltip } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { FormNumericField, FormTextField } from "@/components/UiKit"
import { MAX_SURCHARGES_PER_ROW } from "@/screens/Pricing/components/tables/constants"
import type { SurchargeFormValues } from "@/screens/Pricing/schema/surchargeFormSchema"

interface SurchargeFormProps {
  fields: { id: string }[]
  canAppend: boolean
  canRemove: boolean
  onAppend: () => void
  onRemove: (index: number) => void
}

export const SurchargeForm: FC<SurchargeFormProps> = ({ fields, canAppend, canRemove, onAppend, onRemove }) => {
  const { control } = useFormContext<SurchargeFormValues>()

  return (
    <Box className='flex flex-col gap-y-4'>
      {fields.map((field, index) => (
        <Box key={field.id} className='flex gap-3'>
          <Box className='flex flex-1 gap-3'>
            <FormNumericField
              control={control}
              name={`surcharges.${index}.price`}
              label='Price'
              prefix='$'
              decimalScale={2}
              allowNegative={false}
              data-testid={`pricing.surcharge-form-dialog.price-field-${index}`}
            />
            <FormTextField
              control={control}
              name={`surcharges.${index}.description`}
              label='Description'
              data-testid={`pricing.surcharge-form-dialog.description-field-${index}`}
            />
          </Box>
          <Tooltip title={canRemove ? "" : "At least one surcharge is required"} placement='left'>
            <IconButton
              disabled={!canRemove}
              onClick={() => onRemove(index)}
              data-testid={`pricing.surcharge-form-dialog.remove-button-${index}`}
            >
              <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Box className='flex justify-end'>
        <Tooltip title={canAppend ? "" : `Maximum ${MAX_SURCHARGES_PER_ROW} surcharges per row`} placement='right'>
          <span>
            <Button
              appearance='text'
              startIcon={<AddOutlined />}
              disabled={!canAppend}
              onClick={onAppend}
              data-testid='pricing.surcharge-form-dialog.add-button'
            >
              Additional Surcharge
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}
